import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useStore } from "@/context/StoreContext";
import { useAuth } from "@/context/AuthContext";
import api, { mediaUrl } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { toast } from "sonner";
import { ShieldCheck, Truck, RotateCcw } from "lucide-react";

const DEFAULT_TRUST = [
  "Secure payment — processed by Stripe. We never see or store your card details.",
  "Complimentary UK returns within 14 days [to confirm].",
  "Carefully packaged and dispatched from Glasgow.",
];
const TRUST_ICONS = [ShieldCheck, RotateCcw, Truck];

const CheckoutPage = () => {
  const { bag, settings } = useStore();
  const { format, currency, isConverted } = useCurrency();
  const { user } = useAuth();
  const rawTrust = (settings?.checkout_trust || []).map((t) => (t || "").trim()).filter(Boolean);
  const trust = (rawTrust.length ? rawTrust : DEFAULT_TRUST).slice(0, 3);
  const [email, setEmail] = useState("");
  const [optIn, setOptIn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState("");
  const [discount, setDiscount] = useState(null);
  const [codeMsg, setCodeMsg] = useState("");
  const [gift, setGift] = useState("");
  const [giftInfo, setGiftInfo] = useState(null);
  const [giftMsg, setGiftMsg] = useState("");

  const priced = bag.filter((i) => i.price != null);
  const subtotal = priced.reduce((n, i) => n + i.price * i.qty, 0);
  const discountAmount = discount ? discount.discount_amount : 0;
  const giftUsed = giftInfo ? Math.min(giftInfo.balance, Math.max(0, subtotal - discountAmount)) : 0;
  const total = Math.max(0, subtotal - discountAmount - giftUsed);

  const applyGift = async () => {
    if (!gift.trim()) return;
    setGiftMsg("");
    try {
      const { data } = await api.post("/gift-cards/validate", { code: gift.trim() });
      setGiftInfo(data); toast.success(`Gift card applied · ${format(data.balance)} balance.`);
    } catch (e) {
      setGiftInfo(null);
      setGiftMsg(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "That gift card isn't valid.");
    }
  };

  const applyCode = async () => {
    if (!code.trim()) return;
    setCodeMsg("");
    try {
      const { data } = await api.post("/discounts/validate", { code: code.trim(), subtotal });
      setDiscount(data); setCodeMsg("");
      toast.success(`Code ${data.code} applied.`);
    } catch (e) {
      setDiscount(null);
      setCodeMsg(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "That code isn't valid.");
    }
  };

  const pay = async () => {
    if (!bag.length) return;
    if (!user && !email) { toast("Please enter your email to continue as a guest."); return; }
    if (!user && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast("Please enter a valid email address."); return; }
    setBusy(true);
    try {
      const items = bag.map((i) => ({ product_id: i.productId, colour: i.colour, size: i.size, qty: i.qty }));
      const { data } = await api.post("/payments/checkout", { items, origin_url: window.location.origin, email: user ? undefined : email, discount_code: discount ? discount.code : undefined, gift_card_code: giftInfo ? giftInfo.code : undefined, marketing_opt_in: optIn });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Checkout could not start.");
      setBusy(false);
    }
  };

  if (!bag.length) return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6">
      <p className="font-serif text-4xl mb-4">Your selection is waiting.</p>
      <Link to="/collections/drop-001" className="border border-espresso px-8 py-3.5 text-[0.72rem] tracking-[0.2em] uppercase hover:bg-espresso hover:text-cream transition-colors">Discover DROP 001</Link>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto px-6 pt-14 md:pt-20 pb-24 grid lg:grid-cols-[1fr_380px] gap-12">
      <div>
        <p className="overline text-gold mb-3">Checkout</p>
        <h1 className="font-serif text-5xl leading-none mb-10">Your details.</h1>
        {!user && (
          <div className="mb-8">
            <p className="overline text-espresso/60 mb-2">Contact</p>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email for your order confirmation"
              data-testid="checkout-email" className="w-full bg-cream border border-espresso/20 px-4 py-3.5 text-sm outline-none focus:border-espresso" />
            <p className="text-xs text-espresso/50 mt-2">Have an account? <Link to="/account" className="hover-underline text-espresso">Sign in</Link> — or continue as a guest.</p>
          </div>
        )}
        <p className="text-sm text-espresso/60 leading-relaxed mb-8">Delivery and payment are completed securely on the next screen. We never store your card details. Shipping address is collected at payment.</p>
        <label className="flex items-start gap-2.5 text-sm text-espresso/70 leading-relaxed mb-8 cursor-pointer select-none" data-testid="checkout-optin">
          <input type="checkbox" checked={optIn} onChange={(e) => setOptIn(e.target.checked)} className="mt-1 accent-espresso" />
          Email me news &amp; new drops from NoirbyAmara. You can unsubscribe anytime.
        </label>
        <button onClick={pay} disabled={busy} data-testid="checkout-pay"
          className="w-full lg:w-auto bg-espresso text-cream px-12 py-4 text-[0.72rem] tracking-[0.22em] uppercase hover:bg-cocoa transition-colors disabled:opacity-50">
          {busy ? "Redirecting to secure payment…" : "Continue to Payment"}
        </button>

        <div className="mt-10 border-t border-espresso/10 pt-8 space-y-4" data-testid="checkout-trust">
          {trust.map((t, i) => {
            const Icon = TRUST_ICONS[i] || ShieldCheck;
            return (
              <div key={i} className="flex items-start gap-3">
                <Icon size={17} strokeWidth={1.5} className="text-gold mt-0.5 shrink-0" />
                <p className="text-sm text-espresso/70 leading-relaxed">{t}</p>
              </div>
            );
          })}
          <div className="flex flex-wrap gap-x-5 gap-y-1 pt-1 text-[0.68rem] tracking-[0.14em] uppercase text-espresso/50">
            <Link to="/pages/shipping-delivery" className="hover-underline">Delivery</Link>
            <Link to="/pages/returns-exchanges" className="hover-underline">Returns</Link>
            <Link to="/faq" className="hover-underline">FAQ</Link>
            <Link to="/pages/contact" className="hover-underline">Contact</Link>
          </div>
        </div>
      </div>
      <aside className="bg-sand p-6 h-fit">
        <p className="overline mb-5">Your Selection</p>
        <div className="space-y-4 mb-6">
          {bag.map((i) => (
            <div key={i.key} className="flex gap-3">
              {i.image
                ? <img src={mediaUrl(i.image)} alt={i.name} className="w-14 h-18 object-cover" />
                : <span className="w-14 h-18 bg-cream flex items-center justify-center text-[0.5rem] tracking-[0.12em] uppercase text-espresso/40 text-center leading-tight px-1">Image soon</span>}
              <div className="flex-1">
                <p className="font-serif text-base leading-tight">{i.name}</p>
                <p className="text-xs text-espresso/60">{i.colour} · {i.size} · x{i.qty}</p>
              </div>
              <span className="text-sm">{format(i.price)}</span>
            </div>
          ))}
        </div>
        <div className="border-t border-espresso/10 pt-4">
          <p className="overline text-espresso/60 mb-2">Discount Code</p>
          <div className="flex gap-2 mb-2">
            <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Enter code" data-testid="discount-code-input"
              className="flex-1 bg-cream border border-espresso/20 px-3 py-2.5 text-sm outline-none focus:border-espresso" />
            <button onClick={applyCode} data-testid="discount-apply" className="border border-espresso px-4 text-[0.68rem] tracking-[0.16em] uppercase hover:bg-espresso hover:text-cream transition-colors">Apply</button>
          </div>
          {codeMsg && <p className="text-xs text-wine mb-2" data-testid="discount-error">{codeMsg}</p>}
          {discount && <p className="text-xs text-sage mb-2" data-testid="discount-applied">{discount.code} applied · −{format(discountAmount)}</p>}
        </div>
        <div className="border-t border-espresso/10 pt-4">
          <p className="overline text-espresso/60 mb-2">Gift Card</p>
          <div className="flex gap-2 mb-2">
            <input value={gift} onChange={(e) => setGift(e.target.value.toUpperCase())} placeholder="Gift card code" data-testid="gift-code-input"
              className="flex-1 bg-cream border border-espresso/20 px-3 py-2.5 text-sm outline-none focus:border-espresso" />
            <button onClick={applyGift} data-testid="gift-apply" className="border border-espresso px-4 text-[0.68rem] tracking-[0.16em] uppercase hover:bg-espresso hover:text-cream transition-colors">Apply</button>
          </div>
          {giftMsg && <p className="text-xs text-wine mb-2">{giftMsg}</p>}
          {giftInfo && <p className="text-xs text-sage mb-2" data-testid="gift-applied">{giftInfo.code} · −{format(giftUsed)}</p>}
        </div>
        <div className="border-t border-espresso/10 pt-4 flex justify-between text-sm">
          <span className="text-espresso/60">Subtotal</span>
          <span>{format(subtotal)}</span>
        </div>
        {discount && (
          <div className="flex justify-between text-sm mt-1 text-sage">
            <span>Discount ({discount.code})</span><span>−{format(discountAmount)}</span>
          </div>
        )}
        {giftInfo && giftUsed > 0 && (
          <div className="flex justify-between text-sm mt-1 text-sage">
            <span>Gift card</span><span>−{format(giftUsed)}</span>
          </div>
        )}
        <div className="flex justify-between text-base mt-2 pt-2 border-t border-espresso/10">
          <span>Total</span><span data-testid="checkout-total">{format(total)}</span>
        </div>
        {isConverted && (
          <p className="text-xs text-espresso/50 mt-2" data-testid="checkout-currency-note">
            Prices shown in {currency}. You'll be charged the equivalent in GBP (£{total.toFixed(2)}). Live rates by exchangerate-api.com.
          </p>
        )}
        <p className="text-xs text-espresso/40 mt-2">Taxes and shipping calculated at payment.</p>
      </aside>
    </div>
  );
};

export default CheckoutPage;
